import { useState, useEffect } from "react";
import { useParams, useRouterState, Link, useNavigate } from "@tanstack/react-router";
import { useAuth, useUser } from "@clerk/react";
import { JSONTree } from "react-json-tree";
import type { SourceConfig, SourceSnapshot, DisplayMode } from "@tuit/shared-types";
import { SourcePanel } from "../components/SourcePanel";
import adminStyles from "../styles/components/AdminPage.module.css";
import styles from "../styles/pages/SourceEditorPage.module.css";

const DISPLAY_MODES: DisplayMode[] = ["text", "picture", "gallery"];

const ICON_BG_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Transparent", value: "" },
  { label: "White",       value: "#ffffff" },
  { label: "87% white",   value: "#dedede" },
  { label: "75% white",   value: "#bfbfbf" },
  { label: "62% white",   value: "#9e9e9e" },
  { label: "50% grey",    value: "#808080" },
  { label: "37% black",   value: "#5e5e5e" },
  { label: "25% black",   value: "#404040" },
  { label: "Black",       value: "#000000" },
];

export function SourceEditorPage() {
  const { sourceId } = useParams({ strict: false }) as { sourceId: string };
  const { location } = useRouterState();
  const isFromFeeds = location.pathname.startsWith("/admin/feeds/");
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  // KV snapshot — the saved public state
  const [snapshot, setSnapshot] = useState<SourceSnapshot | null>(null);
  const [rawXml, setRawXml] = useState<string | null>(null);
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [rawError, setRawError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  // Unsaved preview — populated by the Preview button, never touches KV
  const [previewSnapshot, setPreviewSnapshot] = useState<SourceSnapshot | null>(null);
  const [previewRawXml, setPreviewRawXml] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Which data source is the panel showing
  const isShowingPreview = previewSnapshot !== null || previewRawXml !== null;

  // Form fields
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [thumbnails, setThumbnails] = useState(false);
  const [refreshIntervalMinutes, setRefreshIntervalMinutes] = useState(360);
  const [displayMode, setDisplayMode] = useState<DisplayMode>("text");
  const [iconBackground, setIconBackground] = useState("");

  const [committed, setCommitted] = useState<{
    title: string; url: string; thumbnails: boolean;
    refreshIntervalMinutes: number; displayMode: DisplayMode;
    iconBackground: string;
  } | null>(null);

  const [previewView, setPreviewView] = useState<"raw" | "json" | "ux">("ux");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const isDirty =
    committed !== null &&
    (title !== committed.title || url !== committed.url ||
      thumbnails !== committed.thumbnails ||
      refreshIntervalMinutes !== committed.refreshIntervalMinutes ||
      displayMode !== committed.displayMode ||
      iconBackground !== committed.iconBackground);

  function applyConfig(c: SourceConfig) {
    const mode: DisplayMode = c.displayMode ?? "text";
    const bg = c.iconBackground ?? "";
    setTitle(c.title);
    setUrl(c.url);
    setThumbnails(c.thumbnails);
    setRefreshIntervalMinutes(c.refreshIntervalMinutes);
    setDisplayMode(mode);
    setIconBackground(bg);
    setCommitted({ title: c.title, url: c.url, thumbnails: c.thumbnails, refreshIntervalMinutes: c.refreshIntervalMinutes, displayMode: mode, iconBackground: bg });
  }

  useEffect(() => {
    setSnapshot(null);
    setRawXml(null);
    setRawError(null);
    setCommitted(null);
    setLoading(true);
    setSaveSuccess(false);
    setSaveError(null);
    setPreviewSnapshot(null);
    setPreviewRawXml(null);
    setPreviewError(null);
    Promise.all([
      fetch(`/api/source/${sourceId}/config`).then((r) => r.json()),
      fetch(`/api/source/${sourceId}/snapshot`).then((r) => r.json()),
    ]).then(([cfg, snap]) => {
      applyConfig(cfg as SourceConfig);
      setSnapshot(snap as SourceSnapshot);
    }).finally(() => setLoading(false));
  }, [sourceId]);

  async function handlePreview() {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await fetch("/api/sources/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, thumbnails, displayMode, refreshIntervalMinutes, iconBackground }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as { raw: string; snapshot: SourceSnapshot };
      setPreviewRawXml(data.raw);
      setPreviewSnapshot(data.snapshot);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setPreviewing(false);
    }
  }

  function clearPreview() {
    setPreviewSnapshot(null);
    setPreviewRawXml(null);
    setPreviewError(null);
  }

  async function reparseFromRaw(raw: string, overrides: { thumbnails?: boolean } = {}) {
    const effectiveThumbnails = overrides.thumbnails ?? thumbnails;
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await fetch("/api/sources/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, thumbnails: effectiveThumbnails, displayMode, refreshIntervalMinutes, iconBackground, rawXml: raw }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as { raw: string; snapshot: SourceSnapshot };
      setPreviewRawXml(data.raw);
      setPreviewSnapshot(data.snapshot);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Reparse failed");
    } finally {
      setPreviewing(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);
    setSaving(true);
    try {
      const res = await fetch(`/api/source/${sourceId}/config`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, url, thumbnails, refreshIntervalMinutes, displayMode, iconBackground }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      applyConfig(await res.json() as SourceConfig);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
      setSaving(false);
      return;
    }
    setSaving(false);

    // Rebuild the public snapshot now that config is saved
    setRebuilding(true);
    setSnapshot(null);
    clearPreview();
    try {
      await fetch(`/api/source/${sourceId}/snapshot`, { method: "DELETE" });
      const snap = await fetch(`/api/source/${sourceId}/snapshot`).then((r) => r.json());
      setSnapshot(snap as SourceSnapshot);
    } finally {
      setRebuilding(false);
    }
  }

  async function handleViewChange(v: "raw" | "json" | "ux") {
    setPreviewView(v);
    // Lazy-load the KV raw XML only if there's no preview raw XML
    if (v === "raw" && previewRawXml === null && rawXml === null && !loadingRaw) {
      setLoadingRaw(true);
      setRawError(null);
      try {
        const res = await fetch(`/api/source/${sourceId}/raw`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(err.error ?? `HTTP ${res.status}`);
        }
        setRawXml(await res.text());
      } catch (err) {
        setRawError(err instanceof Error ? err.message : "Failed to fetch raw feed");
      } finally {
        setLoadingRaw(false);
      }
    }
  }

  if (!isLoaded || !userId || !isAdmin) return null;

  // Resolve what data the panel actually shows
  const activeSnapshot = previewSnapshot ?? snapshot;
  const activeRawXml = previewRawXml ?? rawXml;

  // Merge live form values that don't require a re-parse — icon background is purely visual
  const displaySnapshot = activeSnapshot ? { ...activeSnapshot, iconBackground: iconBackground || undefined } : null;

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        {isFromFeeds
          ? <Link to="/admin/feeds" className={styles.back}>← Feeds</Link>
          : <Link to="/admin" className={styles.back}>← Admin</Link>
        }
        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            Source Editor <span className={styles.sep}>›</span> {sourceId}
          </h1>
        </div>
      </nav>

      <div className={styles.layout}>
        <div className={styles.formPane}>
          <div className={adminStyles.panel}>
            <div className={adminStyles.panelHeader}>
              <h2 className={adminStyles.panelTitle}>Edit Source</h2>
            </div>
            <div className={adminStyles.panelBody}>
              {loading ? (
                <span className={adminStyles.panelEmpty}>Loading…</span>
              ) : (
                <form onSubmit={handleSave} className={adminStyles.editForm}>
                  <div className={adminStyles.editRow}>
                    <span className={adminStyles.editLabel}>ID</span>
                    <span className={styles.idValue}>{sourceId}</span>
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="se-title" className={adminStyles.editLabel}>Title</label>
                    <input id="se-title" className={adminStyles.editInput} value={title} onChange={(e) => setTitle(e.target.value)} required />
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="se-url" className={adminStyles.editLabel}>RSS URL</label>
                    <input id="se-url" className={adminStyles.editInput} type="url" value={url} onChange={(e) => setUrl(e.target.value)} required />
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="se-refresh" className={adminStyles.editLabel}>Refresh (min)</label>
                    <input id="se-refresh" className={adminStyles.editInput} type="number" min={1} value={refreshIntervalMinutes} onChange={(e) => setRefreshIntervalMinutes(Number(e.target.value))} required />
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="se-thumbnails" className={adminStyles.editLabel}>Thumbnails</label>
                    <input id="se-thumbnails" type="checkbox" checked={thumbnails} onChange={(e) => {
                      const v = e.target.checked;
                      setThumbnails(v);
                      const raw = previewRawXml ?? rawXml;
                      if (raw !== null) reparseFromRaw(raw, { thumbnails: v });
                    }} />
                  </div>
                  <div className={adminStyles.editRow}>
                    <span className={adminStyles.editLabel}>Display Mode</span>
                    <div className={styles.displayModeToggle}>
                      {DISPLAY_MODES.map((m) => (
                        <button key={m} type="button" className={`${styles.displayModeBtn} ${displayMode === m ? styles.displayModeBtnActive : ""}`} onClick={() => setDisplayMode(m)}>
                          {m.charAt(0).toUpperCase() + m.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className={adminStyles.editRow}>
                    <span className={adminStyles.editLabel}>Icon BG</span>
                    <div className={styles.iconBgSwatches}>
                      {ICON_BG_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          title={opt.label}
                          className={`${styles.iconBgSwatch} ${iconBackground === opt.value ? styles.iconBgSwatchActive : ""}`}
                          style={opt.value ? { background: opt.value } : undefined}
                          onClick={() => setIconBackground(opt.value)}
                        >
                          {!opt.value && <span className={styles.iconBgTransparentMark}>✕</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                  {previewError && <div className={adminStyles.opsResultError}>{previewError}</div>}
                  {saveError && <div className={adminStyles.opsResultError}>{saveError}</div>}
                  {saveSuccess && !rebuilding && <div className={styles.saveSuccess}>Saved.</div>}
                  <div className={adminStyles.editActions}>
                    <button type="button" className={adminStyles.actionBtn} onClick={handlePreview} disabled={previewing || saving}>
                      {previewing ? "Previewing…" : "Preview"}
                    </button>
                    <button type="submit" className={adminStyles.actionBtnSecondary} disabled={saving || rebuilding || !isDirty}>
                      {saving ? "Saving…" : rebuilding ? "Publishing…" : "Save"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className={styles.previewPane}>
          <div className={styles.previewPanel}>
            <div className={styles.previewHeader}>
              <div className={styles.previewLabelGroup}>
                <span className={styles.previewLabel}>Preview</span>
                {isShowingPreview && (
                  <span className={styles.previewBadge}>
                    UNSAVED
                    <button type="button" className={styles.previewBadgeClear} onClick={clearPreview} title="Show saved snapshot">×</button>
                  </span>
                )}
              </div>
              <div className={adminStyles.viewToggle}>
                {(["raw", "json", "ux"] as const).map((v) => (
                  <button key={v} type="button" className={`${adminStyles.viewToggleBtn} ${previewView === v ? adminStyles.viewToggleBtnActive : ""}`} onClick={() => handleViewChange(v)}>
                    {v.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {previewView === "raw" && (
              loadingRaw
                ? <div className={adminStyles.panelBody}><span className={adminStyles.panelEmpty}>Fetching raw feed…</span></div>
                : rawError && !activeRawXml
                  ? <div className={adminStyles.panelBody}><span className={adminStyles.opsResultError}>{rawError}</span></div>
                  : activeRawXml
                    ? <pre className={styles.rawPane}>{activeRawXml}</pre>
                    : <div className={adminStyles.panelBody}><span className={adminStyles.panelEmpty}>Click Preview or switch to this tab to load the raw feed.</span></div>
            )}

            {previewView === "json" && (
              displaySnapshot
                ? <div className={styles.jsonPane}><JSONTree data={displaySnapshot} /></div>
                : <div className={adminStyles.panelBody}><span className={adminStyles.panelEmpty}>{rebuilding ? "Publishing snapshot…" : loading ? "Loading…" : "No snapshot — click Preview or Save."}</span></div>
            )}

            {previewView === "ux" && (
              displaySnapshot
                ? <SourcePanel section={displaySnapshot} displayMode={displayMode} />
                : <div className={adminStyles.panelBody}><span className={adminStyles.panelEmpty}>{rebuilding ? "Publishing snapshot…" : loading ? "Loading…" : "No snapshot — click Preview or Save."}</span></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
